import React, { useContext, useState, useEffect } from "react";
import {
  Stack,
  Grid2 as Grid,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
// date picker
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
//Icons
import { BiMinusCircle } from "react-icons/bi";

//Srcs
import "../../../../Style/dialogStyle.scss";
import { AuthContext } from "../../../../Context/AuthContext";
import { translateLauguage } from "../../../../Function/Translate";
import {
  CREATE_CLASS_ATTENDED,
  CHANGE_CLASS,
} from "../../../../Schema/Student";
import {
  SelectAcademicYear,
  SelectSchool,
  SelectClassByAcademicYearAndSchool,
} from "../../../../Function/DynamicSelect";

import DatePickerForDialog from "../../../Include/SelecteDatePicker/DatePickerForDialog";

export default function StudentEnrollmentForm({
  open,
  handleClose,
  setRefetch,
}) {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  const [academicYearVal, setAcademicYearVal] = useState({ id: "", title: "" });
  const [schoolVal, setSchoolVal] = useState({ id: "", title: "" });
  const [attendedClassId, setAttendedClassId] = useState({
    id: "",
    title: "",
  });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let stuId = params.get("id");

  // console.log("stuId", stuId);

  //======================== Create User =========================
  const [createClassAttended] = useMutation(CREATE_CLASS_ATTENDED, {
    onCompleted: ({ createClassAttended }) => {
      // console.log("createClassAttended::", createClassAttended);
      setLoading(false);
      if (createClassAttended?.is_success === true) {
        setAlert(true, "success", createClassAttended?.message);
        handleClose();
        resetForm();
        setRefetch();
        setAttendedClassId({ id: "", title: "" });
        setSchoolVal({ id: "", title: "" });
        setAcademicYearVal({ id: "", title: "" });
      } else {
        setAlert(true, "error", createClassAttended?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });
  //======================== Create User =========================
  const [changeClass] = useMutation(CHANGE_CLASS, {
    onCompleted: ({ changeClass }) => {
      // console.log("changeClass::", changeClass);
      setLoading(false);
      if (changeClass?.is_success === true) {
        setAlert(true, "success", changeClass?.message);
        handleClose();
        resetForm();
        // setRefetch();
      } else {
        setAlert(true, "error", changeClass?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    class_id: Yup.string().required(t("require")),
    join_date: Yup.date().required(t("require")),
    student_id: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      class_id: "",
      join_date: moment(),
      student_id: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      createClassAttended({
        variables: {
          input: {
            class_id: values?.class_id,
            join_date: moment(values?.join_date).format("YYYY-MM-DD"),
            student_id: stuId,
          },
        },
      });
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  useEffect(() => {
    setFieldValue("class_id", attendedClassId?.id);
  }, [attendedClassId?.id]);

  useEffect(() => {
    setFieldValue(academicYearVal?.id);
  }, [academicYearVal?.id]);
  
  useEffect(() => {
    setFieldValue(schoolVal?.id);
  }, [schoolVal?.id]);

  useEffect(() => {
    resetForm();
    setAttendedClassId({ id: "", title: "" });
    setSchoolVal({ id: "", title: "" });
    setAcademicYearVal({ id: "", title: "" });
  }, [open]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {t("dialog_title_create_enrollment")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("academic_year")}
                </Typography>
                <SelectAcademicYear
                  selectedVal={academicYearVal}
                  setSelectedVal={setAcademicYearVal}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("school")}
                </Typography>
                <SelectSchool
                  selectedVal={schoolVal}
                  setSelectedVal={setSchoolVal}
                  disabled={academicYearVal?.id ? false : true}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("class")}
                </Typography>
                <SelectClassByAcademicYearAndSchool
                  academicYearId={academicYearVal?.id}
                  schoolId={schoolVal?.id}
                  selectedVal={attendedClassId}
                  setSelectedVal={setAttendedClassId}
                  disabled={academicYearVal?.id && schoolVal?.id ? false : true}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("enroll_date")}
                </Typography>
                <DatePickerForDialog
                  date={values?.join_date}
                  setDate={(e) => setFieldValue("join_date", e)}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
            >
              {t("btn-create")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
